'use client';

import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IRedux } from '@/_store/types';
import { setDeliveryZone } from '@/_store/common/common.slice';
import { useActiveBrand } from './useActiveBrand';

export type DeliveryZoneValue = {
  id: string;
  min_order_amount: number;
} | null;

/**
 * Returns delivery zone state and setter for the current brand (shipper).
 * Delivery zones are stored per brand, like branches.
 */
export function useDeliveryZone(): [
  DeliveryZoneValue,
  (zone: DeliveryZoneValue) => void,
] {
  const dispatch = useDispatch();
  const shipperId = useActiveBrand();
  const deliveryZone = useSelector(
    (state: IRedux) => state.common.deliveryZones?.[shipperId] ?? null
  );
  const setZone = useCallback(
    (zone: DeliveryZoneValue) => {
      dispatch(setDeliveryZone({ shipper_id: shipperId, zone }));
    },
    [dispatch, shipperId]
  );
  return [deliveryZone, setZone];
}

/**
 * Returns delivery zone for a specific shipper (e.g. when you have shipperId from route/settings).
 */
export function useDeliveryZoneForShipper(shipperId: string): DeliveryZoneValue {
  return useSelector(
    (state: IRedux) => state.common.deliveryZones?.[shipperId] ?? null
  );
}
